import { ReactNode, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../contexts/NavigationContext';
import {
  LogOut,
  LayoutDashboard,
  BookOpen,
  Users,
  FolderOpen,
  Calendar,
  MessageSquare,
  FileText,
  Menu,
  X,
  Settings,
  Award,
  Mail,
  Link2,
  LineChart,
  FileWarning,
  AlertTriangle,
  ClipboardList,
  Activity,
  ClipboardCheck,
  BarChart2,
  type LucideIcon,
} from 'lucide-react';

interface LayoutProps {
  children: ReactNode;
}

interface NavItem {
  icon: LucideIcon;
  label: string;
  view: string;
}

export function Layout({ children }: LayoutProps) {
  const { profile, signOut } = useAuth();
  const { navigateTo, currentView } = useNavigation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const getNavigationItems = (): NavItem[] => {
    switch (profile?.role) {
      case 'admin':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', view: 'dashboard' },
          { icon: Users, label: 'Usuarios', view: 'users' },
          { icon: BookOpen, label: 'Cursos', view: 'courses' },
          { icon: FolderOpen, label: 'Biblioteca', view: 'library' },
          { icon: ClipboardCheck, label: 'Entregables', view: 'entregables' },
          { icon: FileWarning,    label: 'Entregas BIM',     view: 'bim-entregas' },
          { icon: AlertTriangle,  label: 'Alumnos en Riesgo', view: 'alumnos-riesgo' },
          { icon: ClipboardList,  label: 'Tareas Pendientes', view: 'tareas-pendientes' },
          { icon: BarChart2,      label: 'Resultados Tareas', view: 'tareas-resultados' },
          { icon: Activity,       label: 'Actividad Docente', view: 'actividad-docente' },
          { icon: Calendar, label: 'Calendario', view: 'calendar' },
          { icon: FileText, label: 'Reportes', view: 'reports' },
          { icon: Mail, label: 'Envío de Correos', view: 'emails' },
          { icon: Link2, label: 'Gestión de Links', view: 'links' },
        ];
      case 'docente':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', view: 'dashboard' },
          { icon: BookOpen, label: 'Mis Cursos', view: 'courses' },
          { icon: FolderOpen, label: 'Biblioteca', view: 'library' },
          { icon: ClipboardCheck, label: 'Entregables', view: 'entregables' },
          { icon: Calendar, label: 'Calendario', view: 'calendar' },
          { icon: MessageSquare, label: 'Chat', view: 'chat' },
          { icon: Link2, label: 'Gestión de Links', view: 'links' },
          { icon: BarChart2, label: 'Resultados Tareas', view: 'tareas-resultados' },
          { icon: LineChart, label: 'Dashboard BIM', view: 'bim-dashboard' },
        ];
      case 'alumno':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', view: 'dashboard' },
          { icon: BookOpen, label: 'Mis Cursos', view: 'courses' },
          { icon: FolderOpen, label: 'Biblioteca', view: 'library' },
          { icon: ClipboardCheck, label: 'Entregables', view: 'entregables' },
          { icon: Calendar, label: 'Calendario', view: 'calendar' },
          { icon: MessageSquare, label: 'Chat', view: 'chat' },
          { icon: Award, label: 'Notas', view: 'grades' },
          { icon: Link2, label: 'Links Útiles', view: 'links' },
          { icon: LineChart, label: 'Dashboard BIM', view: 'bim-dashboard' },
        ];
      default:
        return [];
    }
  };

  const navigationItems = getNavigationItems();
  const roleLabel = profile?.role
    ? profile.role.charAt(0).toUpperCase() + profile.role.slice(1)
    : '';

  const initials = profile?.full_name
    ? profile.full_name
        .split(' ')
        .map((p) => p[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '?';

  return (
    <div className="relative h-screen flex overflow-hidden">
      <div
        className="orb orb-lime w-[600px] h-[600px] -top-48 -left-32 opacity-30"
        aria-hidden="true"
      />
      <div
        className="orb orb-violet w-[500px] h-[500px] -bottom-48 right-0 opacity-25"
        style={{ animationDelay: '6s' }}
        aria-hidden="true"
      />

      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:bg-cubus-lime focus:text-cubus-bg focus:px-3 focus:py-2 focus:rounded-lg focus:font-medium"
      >
        Saltar al contenido principal
      </a>

      <aside
        aria-label="Navegación principal"
        className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50 w-72 transition-transform duration-300 relative-z`}
      >
        <div className="h-full flex flex-col glass-panel border-r border-white/[0.06]">
          <div className="p-6 border-b border-white/[0.06]">
            <div className="flex items-center gap-3">
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 bg-cubus-lime/30 blur-lg rounded-full" />
                <img
                  src="/logo_plataforma.svg"
                  alt="Cubus"
                  width={40}
                  height={40}
                  style={{ width: 40, height: 'auto', position: 'relative' }}
                />
              </div>
              <div className="min-w-0">
                <h1 className="text-white font-bold text-display tracking-tight">Intranet</h1>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-cubus-lime animate-pulse" />
                  <p className="text-xs text-white/60">{roleLabel}</p>
                </div>
              </div>
            </div>
          </div>

          <nav
            className="p-3 space-y-1 overflow-y-auto no-scrollbar"
            aria-label="Menú principal"
          >
            {navigationItems.map((item) => {
              const isActive = currentView === item.view;
              return (
                <button
                  key={item.view}
                  onClick={() => {
                    navigateTo(item.view);
                    setSidebarOpen(false);
                  }}
                  aria-current={isActive ? 'page' : undefined}
                  className={`group relative w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 focus-lime ${
                    isActive
                      ? 'bg-gradient-to-r from-cubus-lime/20 via-cubus-lime/10 to-transparent text-white font-semibold border border-cubus-lime/30 shadow-glow-lime-sm'
                      : 'text-white/70 hover:text-white hover:bg-white/[0.04] border border-transparent'
                  }`}
                >
                  {isActive && (
                    <span
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r bg-cubus-lime shadow-glow-lime-sm"
                      aria-hidden="true"
                    />
                  )}
                  <item.icon
                    className={`w-5 h-5 flex-shrink-0 transition-colors ${
                      isActive ? 'text-cubus-lime' : 'text-white/50 group-hover:text-white/80'
                    }`}
                    aria-hidden="true"
                  />
                  <span className="truncate text-[14px]">{item.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="p-4 border-t border-white/[0.06] space-y-2">
            <button
              onClick={() => {
                navigateTo('profile');
                setSidebarOpen(false);
              }}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.04] transition-all focus-lime group"
            >
              <div className="relative flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-gradient-lime flex items-center justify-center text-cubus-bg font-bold text-sm">
                  {initials}
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-cubus-lime border-2 border-cubus-deep" />
              </div>
              <div className="min-w-0 flex-1 text-left">
                <p className="text-white font-medium truncate text-sm">{profile?.full_name}</p>
                <p className="text-xs text-white/50 truncate">{profile?.email}</p>
              </div>
              <Settings
                className="w-4 h-4 text-white/30 group-hover:text-white/70 transition-colors"
                aria-hidden="true"
              />
            </button>
            <button onClick={signOut} className="btn-danger w-full" aria-label="Cerrar sesión">
              <LogOut className="w-4 h-4" aria-hidden="true" />
              <span className="text-sm">Cerrar Sesión</span>
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-md z-40"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <main id="main-content" className="relative-z flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
        <header className="lg:hidden glass-deep border-b border-white/[0.06] px-4 py-3 flex-shrink-0 z-30">
          <button
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label={sidebarOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={sidebarOpen}
            className="p-2 -ml-2 text-white/70 hover:text-white rounded-lg focus-lime transition-colors"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </header>

        <div className="flex-1 flex flex-col min-h-0 overflow-hidden animate-fade-in">
          {children}
        </div>
      </main>
    </div>
  );
}
